import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from '../../SvgIcon';
import { DrawerBottom } from '../../drawer';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {any[]} props.selectedItems - Selected items.
 * @param {Function} props.getItemText - Given an item, get the text that
 *    represents the item.
 * @param {Function} props.getItemLabel - Given an item, get the label for the
 *    item. It can be text or some JSX.
 * @param {Function} props.isItemInSelectedItems - Returns true if the item is
 *    in the selected items, else false.
 * @param {Function} props.toggleSelectedItem - If the item is not in the
 *    selected items it is added to, else removed from the selected items.
 * @param {Function} props.clearSelectedItems - Clears the selected items.
 * @returns {JSX.Element} Component.
 */
export const SelectMultipleMobile = ({
    label,
    items,
    selectedItems,
    getItemText,
    getItemLabel,
    isItemInSelectedItems,
    toggleSelectedItem,
    clearSelectedItems
}) => {
    /**
     * This queue of selected item changes is necessary to avoid errors where
     * React tries to render this component at its parent component at the same
     * time.
     */
    const [selectedItemQueue, setSelectedItemQueue] = useState([]);
    useEffect(() => {
        if (!selectedItemQueue.length) {
            return;
        }
        selectedItemQueue.forEach((q) => toggleSelectedItem(q));
        setSelectedItemQueue([]);
    }, [selectedItemQueue]);

    const {
        getToggleButtonProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        getLabelProps
    } = useSelect({
        items,
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;
            const { selectedItem } = changes;

            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.ItemClick:
                    setSelectedItemQueue((q) => [...q, selectedItem]);

                    return {
                        ...changes,
                        highlightedIndex: items.findIndex((i) =>
                            // @ts-ignore
                            _.isEqual(i, selectedItem)
                        ),
                        isOpen: true
                    };
                default:
                    return changes;
            }
        }
    });

    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <div className="select-multiple">
                <div
                    className={classNames('select-multiple__display-wrapper', {
                        'select-multiple__display-wrapper--has-selected-items':
                            !!selectedItems.length
                    })}
                >
                    <button
                        type="button"
                        className="select-multiple__display"
                        onClick={() => setIsOpen(true)}
                    >
                        <div className="select-multiple__current">
                            {!selectedItems.length && label}

                            {selectedItems.map((selectedItem, index) => (
                                <span key={`selected-item-${index}`}>
                                    {index !== 0 && ', '}
                                    {getItemText(selectedItem) || ''}
                                </span>
                            ))}
                        </div>

                        {!selectedItems.length && (
                            <SvgIcon
                                className="select-multiple__chevron"
                                icon="chevron-down"
                            />
                        )}
                    </button>
                    {!!selectedItems.length && (
                        <button
                            className="select-multiple__reset"
                            onClick={() => clearSelectedItems()}
                        >
                            <SvgIcon
                                className="select-multiple__reset-icon"
                                icon="close-circle-fill"
                            />
                        </button>
                    )}
                </div>
            </div>

            <DrawerBottom
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                heading={label}
            >
                <div className="select-multiple select-multiple--mobile select-multiple--open">
                    {/*
                        An invisible label for accessibility purposes.
                    */}
                    <label className="u-hide" {...getLabelProps()}>
                        {label}
                    </label>

                    {/*
                        An invisible dummy toggle button just so we're calling
                        getToggleButtonProps to ensure rc-drawer doesn't crash.
                    */}
                    <button
                        className="u-hide"
                        {...getToggleButtonProps()}
                    ></button>

                    <div className="select-multiple__options-wrapper">
                        <ul
                            {...getMenuProps()}
                            className={classNames(
                                'select-multiple__options-list',
                                {
                                    'u-hide': !isOpen
                                }
                            )}
                        >
                            {items.map((item, index) => {
                                const itemText = getItemLabel(item);
                                const isSelected = isItemInSelectedItems(item);

                                return (
                                    <li
                                        key={`${itemText}${index}`}
                                        {...getItemProps({ item, index })}
                                        className={classNames(
                                            'select-multiple__option',
                                            {
                                                'select-multiple__option--selected':
                                                    isSelected,
                                                'select-multiple__option--highlighted':
                                                    highlightedIndex === index
                                            }
                                        )}
                                    >
                                        {isSelected ? (
                                            <SvgIcon
                                                className="select-multiple__checkmark"
                                                icon="checkmark-square"
                                            />
                                        ) : (
                                            <SvgIcon
                                                className="select-multiple__checkmark-empty"
                                                icon="checkmark-square-empty"
                                            />
                                        )}

                                        <span className="select-multiple__option-label-wrapper">
                                            {getItemLabel(item)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </DrawerBottom>
        </>
    );
};

SelectMultipleMobile.propTypes = {
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.any).isRequired,
    selectedItems: PropTypes.arrayOf(PropTypes.any).isRequired,
    getItemText: PropTypes.func.isRequired,
    getItemLabel: PropTypes.func.isRequired,
    isItemInSelectedItems: PropTypes.func.isRequired,
    toggleSelectedItem: PropTypes.func.isRequired,
    clearSelectedItems: PropTypes.func.isRequired
};
